
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const useProductAnalytics = (productId?: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: productAnalytics, isLoading: analyticsLoading } = useQuery({
    queryKey: ['product-analytics'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('product_analytics')
        .select(`
          *,
          products (
            name,
            price,
            status
          )
        `)
        .order('date', { ascending: false })
        .limit(100);
      
      if (error) throw error;
      return data;
    },
  });

  const { data: topProducts, isLoading: topProductsLoading } = useQuery({
    queryKey: ['top-products'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select(`
          id,
          name,
          price,
          stock_quantity,
          featured,
          status,
          product_analytics (
            sales_count,
            revenue,
            views_count
          )
        `)
        .eq('status', 'active')
        .order('featured', { ascending: false })
        .limit(10);
      
      if (error) throw error;
      
      // Process the data to get aggregated analytics
      return data.map(product => ({
        ...product,
        total_sales: product.product_analytics?.reduce((sum, analytics) => sum + (analytics.sales_count || 0), 0) || 0,
        total_revenue: product.product_analytics?.reduce((sum, analytics) => sum + (analytics.revenue || 0), 0) || 0,
        total_views: product.product_analytics?.reduce((sum, analytics) => sum + (analytics.views_count || 0), 0) || 0,
      })).sort((a, b) => b.total_revenue - a.total_revenue);
    },
  });

  const { data: lowStockProducts, isLoading: lowStockLoading } = useQuery({
    queryKey: ['low-stock-products'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select(`
          id,
          name,
          stock_quantity,
          status,
          stock_alerts (
            threshold_quantity,
            is_active
          )
        `)
        .eq('status', 'active')
        .order('stock_quantity', { ascending: true })
        .limit(20);
      
      if (error) throw error;
      
      // Filter products that are below their threshold or have very low stock
      return data.filter(product => {
        const threshold = product.stock_alerts?.[0]?.threshold_quantity || 10;
        return product.stock_quantity <= threshold;
      });
    },
  });

  // Fetch stock alerts
  const {
    data: stockAlerts = [],
    isLoading: isLoadingStockAlerts
  } = useQuery({
    queryKey: ['stock-alerts', productId],
    queryFn: async () => {
      const query = supabase
        .from('stock_alerts')
        .select(`
          *,
          product:products(name, stock_quantity)
        `)
        .eq('is_active', true);

      if (productId) {
        query.eq('product_id', productId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    enabled: !!productId || productId === undefined,
  });

  // Update stock alert mutation
  const updateStockAlertMutation = useMutation({
    mutationFn: async ({ 
      productId, 
      thresholdQuantity 
    }: { 
      productId: string; 
      thresholdQuantity: number; 
    }) => {
      const { error } = await supabase
        .from('stock_alerts')
        .upsert({
          product_id: productId,
          threshold_quantity: thresholdQuantity,
          is_active: true
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-alerts'] });
      toast({
        title: 'Success',
        description: 'Stock alert updated successfully',
      });
    },
    onError: (error) => {
      console.error('Failed to update stock alert:', error);
      toast({
        title: 'Error',
        description: 'Failed to update stock alert',
        variant: 'destructive',
      });
    },
  });

  // Computed metrics
  const totalProducts = topProducts?.length || 0;
  const totalRevenue = topProducts?.reduce((sum, product) => sum + product.total_revenue, 0) || 0;
  const totalSales = topProducts?.reduce((sum, product) => sum + product.total_sales, 0) || 0;
  const averageOrderValue = totalSales > 0 ? totalRevenue / totalSales : 0;

  const isLoading = analyticsLoading || topProductsLoading || lowStockLoading;

  return {
    productAnalytics,
    topProducts,
    lowStockProducts,
    metrics: {
      totalProducts,
      totalRevenue,
      totalSales,
      averageOrderValue,
    },
    isLoading,
    stockAlerts,
    isLoadingStockAlerts,
    updateStockAlert: updateStockAlertMutation.mutate,
    isUpdatingStockAlert: updateStockAlertMutation.isPending,
  };
};
